import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { DropDownButtonWithRouter } from "../DropDownButton";

import { Link } from "react-router-dom";

import {
	listTenants,
	setFirmHidden,
	setTenantSuspended,
} from "../../actions/adminActions";
import { openGlobalDialog } from "../../actions/globalEditDialogActions";

import CustomizableTable from "../Tables/CustomizableTable";
import InteractiveTableCell from "../Tables/InteractiveTableCell";
import WipeTenantDialog from "./WipeTenantDialog";
import { Pill } from "../Tables/CellWithPills";
import { humanFileSize, numberWithCommas } from "../../utilities/utilities";
import { firmStatusPillConfig, getFirmStatusPillLabel } from "./firmStatusPill";

import buildData from "../../buildData/buildData";

import lxwConstants from "../../config/lexworkplace-constants";
import { IconBriefcase, IconUser } from "@tabler/icons-react";
import { LexWorkplaceGavelIcon } from "../LexWorkplaceIcons";
import { ID_COLUMN_WIDTH } from "../Tables/SortableTable";
const lexWorkplaceEditions = lxwConstants.lexWorkplaceEditions;
const tenantPlanTypes = lxwConstants.tenantPlanTypes;

const defaultTabFilterValue =
	buildData.branch === "production" ? "Active Firms" : "Testing";

const filterMap = {
	All: () => true,
	"Active Firms": (row) => {
		return (
			row.id > 1000 &&
			row.planType !== tenantPlanTypes.options.HIDDEN &&
			row.planType !== tenantPlanTypes.options.SUSPENDED &&
			row.activeUserCount >= 1
		);
	},
	Customers: (row) => {
		return (
			row.id > 1000 &&
			row.planType !== tenantPlanTypes.options.HIDDEN &&
			row.planType !== tenantPlanTypes.options.SUSPENDED
		);
	},
	Testing: (row) => {
		return row.id <= 1000;
	},
	"Inactive Firms": (row) => {
		return (
			row.activeUserCount === 0 &&
			row.planType !== tenantPlanTypes.options.HIDDEN &&
			row.planType !== tenantPlanTypes.options.SUSPENDED
		);
	},
	Hidden: (row) => {
		return row.planType === tenantPlanTypes.options.HIDDEN;
	},
	Suspended: (row) => {
		return row.planType === tenantPlanTypes.options.SUSPENDED;
	},
};

const tabDescriptions = {
	All: "All Firms",
	"Active Firms": "Customers with at least 1 active user",
	Customers:
		"All non-hidden firms including those who have terminated service",
	Testing: "Testing Firms.  Those with tenantID < 1000",
	"Inactive Firms": "Un-hidden firms with 0 active users",
	Hidden: "Hidden firms, for mistakes",
	Suspended:
		"Suspended - Firms which are in a permanently terminated state.  All data either can be wiped, or already has been wiped.",
};

const suspendFirmDialogText = `This will move the firm to a "Suspended" state. Only do this if:
1. The firm will no longer be used in any way
2. All of its data is ready to be permanently deleted

Suspending does not delete any data - it just marks the firm as ready for that data to be wiped.`;

function getDocumentsCountForTab(tenantList, filterName) {
	return tenantList
		.filter(filterMap[filterName])
		.reduce((acc, val) => acc + (val.documentCount || 0), 0);
}

function Admin() {
	const dispatch = useDispatch();
	const isSuperAdmin = useSelector((state) =>
		Boolean(
			state.user?.cognitoUser?.cognitoGroups?.includes(
				"LexWorkplace_Super_Admin"
			)
		)
	);
	const [tenants, setTenants] = useState([]);
	const [filter, setFilter] = useState(defaultTabFilterValue);
	const [loading, setLoading] = useState(true);
	const [dataUsageReportGenerated, setDataUsageReportGenerated] =
		useState(null);
	const [totalDataHuman, setTotalDataHuman] = useState();
	const [totalDocumentsInThisView, setTotalDocumentsInThisView] = useState();
	const [wipeDialogTenant, setWipeDialogTenant] = useState(null);

	const filterRef = useRef(filter);
	filterRef.current = filter;

	const loadTenantData = useCallback(() => {
		setLoading(true);
		dispatch({
			type: "LIST_TENANTS_REQUEST",
		});
		listTenants()
			.then((res) => {
				dispatch({
					type: "LIST_TENANTS_SUCCESS",
				});
				var tenants = res.mysql.tenants;
				var dateReportGenerated =
					"Data usage report could not be loaded";
				var totalDataHuman = "???";

				if (res.mysql.dataUsageV2) {
					dateReportGenerated = new Date(
						res.mysql.dataUsageV2.info.reportFinished
					);
					totalDataHuman =
						res.mysql.dataUsageV2.aggregate.total.sizeHuman;
				}
				const totalDocs = getDocumentsCountForTab(
					tenants,
					filterRef.current
				);

				const dataUsageByTenantID = {};
				(res.mysql.dataUsage?.byFirm || []).forEach((firm) => {
					dataUsageByTenantID[firm.tenantID] = firm;
				});

				const processedTenants = tenants.map((ten) => {
					const dataUsageLimitBytes =
						ten.tenantLimits && ten.tenantLimits.STORAGE_LIMIT_GB
							? 1000 *
								1000 *
								1000 *
								ten.tenantLimits.STORAGE_LIMIT_GB
							: 0;

					const archiveDataUsageLimitBytes =
						ten.tenantLimits &&
						ten.tenantLimits.ARCHIVE_STORAGE_LIMIT_GB
							? 1000 *
								1000 *
								1000 *
								ten.tenantLimits.ARCHIVE_STORAGE_LIMIT_GB
							: 0;

					let previewDaysRemaining = null;
					if (ten.activeEditionPreview?.expirationDate) {
						const msRemaining =
							new Date(
								ten.activeEditionPreview.expirationDate
							).getTime() - Date.now();
						if (msRemaining > 0) {
							previewDaysRemaining = Math.ceil(
								msRemaining / (1000 * 60 * 60 * 24)
							);
						}
					}

					const getAmountOverLimit = (usageBytes, limitBytes) => {
						return usageBytes > limitBytes
							? usageBytes - limitBytes
							: null;
					};

					return {
						...ten,
						dataUsageLimitBytes,
						dataUsageOverLimit: getAmountOverLimit(
							ten.dataUsageBytesV2,
							dataUsageLimitBytes
						),
						archiveDataUsageLimitBytes,
						archiveDataUsageOverLimit: getAmountOverLimit(
							ten.archiveDataUsageBytes,
							archiveDataUsageLimitBytes
						),
						searchIndexDocs: ten.searchIndex.docsCount,
						searchIndexError: ten.searchIndex.error || null,
						lxwEditionDisplay: lexWorkplaceEditions.options[
							ten.lxwEdition
						]
							? lexWorkplaceEditions.options[ten.lxwEdition]
									.display
							: "???",
						ocrEnabled: ten.tenantFeatures.automaticOCR
							? "On"
							: "Off",
						previewDaysRemaining,
						//sentinel "-" (rather than blank) lets the column filter find rows with no delete status, same as searchIndexDocs' negative sentinels
						wipeStatus: ten.latestTenantWipeEvent?.event || "-",
						// From the daily S3 inventory report (dataUsage.byFirm), not the
						// live DB counts above - this is an actual S3 object count
						// (including old versions), used to estimate tenant wipe scan
						// progress. May be undefined if the report hasn't run yet.
						expectedS3ObjectCount:
							dataUsageByTenantID[ten.id]?.totalObjects,
					};
				});

				setLoading(false);
				setTenants(processedTenants);
				setDataUsageReportGenerated(dateReportGenerated.toString());
				setTotalDataHuman(totalDataHuman);
				setTotalDocumentsInThisView(totalDocs);
			})
			.catch(() => {
				console.error("error getting /admin/listTenants");
				dispatch({
					type: "LIST_TENANTS_FAILURE",
				});
			});
	}, []);

	useEffect(() => {
		loadTenantData();
	}, [loadTenantData]);

	const columnConfig = useMemo(() => {
		const columns = [
			{
				Header: "ID",
				accessor: "id",
				width: ID_COLUMN_WIDTH,
				Cell: (row) => (
					<InteractiveTableCell copyButtonValue={row.value}>
						{row.value}
					</InteractiveTableCell>
				),
			},
			{
				Header: "Customer ID",
				accessor: "customerID",
				Cell: (row) => (
					<InteractiveTableCell copyButtonValue={row.value}>
						{row.value}
					</InteractiveTableCell>
				),
			},
			{
				Header: "Name",
				accessor: "name",
				width: 340,
				Cell: (row) => {
					const showStatusPill =
						filter !== "Hidden" && filter !== "Suspended";
					const pillLabel = showStatusPill
						? getFirmStatusPillLabel(row.original.planType)
						: null;
					return (
						<InteractiveTableCell
							menuItems={[
								{
									text: "Edit Firm",
									href: `/admin/tenant/edit/${row.original.id}`,
								},
								{
									text:
										row.original.planType ===
										tenantPlanTypes.options.HIDDEN
											? "Un-Hide Firm"
											: "Hide Firm",
									disabled:
										row.original.planType ===
										tenantPlanTypes.options.SUSPENDED,
									disabledHoverText: "This firm is suspended",
									onSelect: () =>
										dispatch(
											setFirmHidden(
												row.original.id,
												row.original.planType !==
													tenantPlanTypes.options
														.HIDDEN,
												loadTenantData
											)
										),
								},
								row.original.planType ===
								tenantPlanTypes.options.SUSPENDED
									? {
											text: "Unsuspend Firm",
											onSelect: () =>
												dispatch(
													setTenantSuspended(
														row.original.id,
														false,
														loadTenantData
													)
												),
										}
									: {
											text: "Suspend Firm",
											disabled:
												row.original.enabledUserCount >
												0,
											disabledHoverText:
												"Only a firm with all users disabled may be suspended",
											onSelect: () =>
												dispatch(
													openGlobalDialog(
														{
															label: `Suspend ${row.original.name}?`,
															mainText:
																suspendFirmDialogText,
															canSubmit: true,
															hideTextfield: true,
															submitButtonLabel:
																"Suspend Firm",
															redSubmitButton: true,
														},
														() =>
															dispatch(
																setTenantSuspended(
																	row.original
																		.id,
																	true,
																	loadTenantData
																)
															)
													)
												),
										},
								row.original.planType ===
								tenantPlanTypes.options.SUSPENDED
									? {
											text: "Wipe Tenant...",
											color: "var(--lw-darker-red)",
											disabled: !isSuperAdmin,
											disabledHoverText:
												"Only a Super Admin can wipe a tenant",
											onSelect: () =>
												setWipeDialogTenant(
													row.original
												),
										}
									: null,
							]}
							copyButtonValue={row.original.name}
						>
							<Link to={`/admin/tenant/${row.original.id}/users`}>
								{row.original.name}
							</Link>
							{pillLabel ? (
								<Pill
									label={pillLabel}
									style={
										firmStatusPillConfig[pillLabel].style
									}
									title={
										firmStatusPillConfig[pillLabel].title
									}
								/>
							) : null}
						</InteractiveTableCell>
					);
				},
			},
		];

		if (filter === "Suspended") {
			columns.push({
				Header: "Delete Status",
				accessor: "wipeStatus",
				unhidable: true,
				Cell: (row) =>
					row.value !== "-" ? (
						<Pill
							label={row.value}
							style={{
								backgroundColor: "var(--lw-light-red)",
								color: "black",
							}}
							title={
								row.original.latestTenantWipeEvent
									? new Date(
											row.original.latestTenantWipeEvent
												.timestamp
										).toLocaleString()
									: undefined
							}
						/>
					) : null,
			});
		}

		columns.push(
			{
				Header: "Region",
				accessor: "regionName",
				Cell: (row) => (
					<div>
						{row.original.tenantStatus !== "VALID" && (
							<span style={{ color: "red" }}>
								{row.original.tenantStatus + " "}
							</span>
						)}
						<span>{row.value}</span>
					</div>
				),
			},
			{
				Header: "Edition",
				accessor: "lxwEditionDisplay",
			},
			{
				Header: "Preview",
				accessor: "previewDaysRemaining",
				Cell: (row) => (row.value != null ? `${row.value}d left` : ""),
			},
			{
				Header: "Active Users",
				accessor: "activeUserCount",
				//if 0 active users, show a blank Cell rather than a "0"
				Cell: (row) => row.value || "",
			},
			{
				Header: "Data Usage",
				accessor: "dataUsageBytesV2",
				emptyFilterText: "-",
				Cell: (row) =>
					row.value ? humanFileSize(row.value, true) : "",
			},
			{
				Header: "Data Usage Limit",
				accessor: "dataUsageLimitBytes",
				Cell: (row) => humanFileSize(row.value, true),
			},
			{
				Header: "Data Usage Over Limit",
				accessor: "dataUsageOverLimit",
				emptyFilterText: "-",
				Cell: (row) =>
					row.value ? humanFileSize(row.value, true) : "",
			},
			{
				Header: "Archive Data Usage",
				accessor: "archiveDataUsageBytes",
				Cell: (row) =>
					row.value ? humanFileSize(row.value, true) : "",
			},
			{
				Header: "Archive Data Usage Limit",
				accessor: "archiveDataUsageLimitBytes",
				Cell: (row) => humanFileSize(row.value, true),
			},
			{
				Header: "Archive Data Usage Over Limit",
				accessor: "archiveDataUsageOverLimit",
				Cell: (row) => {
					return row.value ? humanFileSize(row.value, true) : "";
				},
			},
			{
				Header: "Documents",
				accessor: "documentCount",
				Cell: (row) => numberWithCommas(row.value),
			},
			{
				Header: "Emails",
				accessor: "emailCount",
				Cell: (row) => numberWithCommas(row.value),
			},
			{
				Header: "Matters",
				accessor: "matterCount",
				emptyFilterText: "-",
				//if 0 matters, show a blank Cell rather than a "0"
				Cell: (row) => (row.value ? numberWithCommas(row.value) : ""),
			},
			{
				Header: "Search Index",
				accessor: "searchIndexDocs",
				Cell: (row) =>
					row.original.searchIndexError ? (
						<span style={{ color: "var(--lw-darker-red)" }}>
							{row.original.searchIndexError}
						</span>
					) : (
						numberWithCommas(row.value)
					),
			},
			{
				Header: "OCR",
				accessor: "ocrEnabled",
				Cell: (row) => (row.value === "On" ? "On" : ""),
			}
		);

		return columns;
	}, [dispatch, loadTenantData, filter]);

	return (
		<div className="fullHeightFlex noDefaultMargin">
			<div style={{ padding: 8 }}>
				{" "}
				<div>
					<span style={{ marginRight: 4 }}>Total Data Volume:</span>
					<span style={{ fontWeight: "bold" }}>
						{totalDataHuman}
					</span>{" "}
					(Includes inactive and testing firms)
				</div>
				<div>
					<span style={{ marginRight: 4 }}>Total Documents:</span>
					<span style={{ fontWeight: "bold" }}>
						{numberWithCommas(totalDocumentsInThisView)}
					</span>{" "}
					(Among firms in current Tab)
				</div>
				<div>
					Data Usage Calculated: {dataUsageReportGenerated || ""}
				</div>
			</div>

			{tenants && (
				<div className="fullHeightFlex">
					<CustomizableTable
						className="fullWidth"
						actionButtons={({ openCsvExport }) => (
							<div
								style={{
									display: "flex",
									gap: 8,
								}}
							>
								<DropDownButtonWithRouter
									title="Create New Firm"
									iconComponent={
										<IconBriefcase
											size={16}
											style={{ verticalAlign: "middle" }}
										/>
									}
									pullRight
									menuItems={Object.keys(
										lexWorkplaceEditions.options
									).map((editionCode) => {
										const { display } =
											lexWorkplaceEditions.options[
												editionCode
											];
										return {
											text: `New ${display} Firm`,
											href: `/admin/tenant/new/${editionCode}`,
										};
									})}
								/>
								<DropDownButtonWithRouter
									title="Actions"
									iconComponent={<LexWorkplaceGavelIcon />}
									pullRight
									menuItems={[
										{
											text: "Export Data as CSV",
											onSelect: openCsvExport,
										},
									]}
								/>
							</div>
						)}
						csvExport={{
							extraColumns: [
								{
									Header: "Status",
									accessor: (row) =>
										row.planType ===
										tenantPlanTypes.options.SUSPENDED
											? "Suspended"
											: row.planType ===
												  tenantPlanTypes.options.HIDDEN
												? "Hidden"
												: "Active",
								},
							],
							csvColumnRenderers: {
								dataUsageBytes: (val) =>
									humanFileSize(val, true),
								dataUsageLimitBytes: (val) =>
									humanFileSize(val, true),
								dataUsageOverLimit: (val) =>
									humanFileSize(val, true),
							},
							fileName: "firms-export",
						}}
						loading={loading}
						tabsFilterMap={filterMap}
						tabDescriptions={tabDescriptions}
						defaultTabFilterValue={defaultTabFilterValue}
						tabFilterChanged={(newTabFilterName) => {
							const totalDocs = getDocumentsCountForTab(
								tenants,
								newTabFilterName
							);
							setFilter(newTabFilterName);
							setTotalDocumentsInThisView(totalDocs);
						}}
						leftSideIconComponent={
							<IconUser
								size={24}
								style={{ alignSelf: "center", margin: "0 8px" }}
							/>
						}
						data={tenants}
						autoResize
						columns={columnConfig}
						noResultsText={"No items here"}
						tableUniqueID="/admin/firms"
					/>
				</div>
			)}
			<WipeTenantDialog
				tenant={wipeDialogTenant}
				onClose={() => setWipeDialogTenant(null)}
				onComplete={loadTenantData}
			/>
		</div>
	);
}

export default Admin;
